import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import useMediaQuery from './hooks/useMediaQuery';

// Shared Components
import Header from './components/header';
import Footer from './components/footer';
import Sidebar from './components/sidebar';
import InitialStatusCheck from './components/InitialStatusCheck';
import { StatusMonitor } from './context/StatusMonitor';
import LoadingAnimation from './components/loadingAnimation';
import ProtectedRoute from './components/ProtectedRoute';

// Desktop Pages
import FTSOSubmissions from './pages/FTSOSubmissions';
import FTSOPerformance from './pages/FTSOPerformance';
import FastUpdates from './pages/FastUpdates';
import Wallets from './pages/Wallets';
import FTSOSummary from './pages/FTSOSummary';
import Nodes from './pages/nodes';
import Indexers from './pages/Indexers';
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import Validators from './pages/Validators';
import FDCSubmissions from './pages/FDCSubmissions';
import RuntimeLogs from './pages/RuntimeLogs';
import RuntimeLogDetail from './pages/RuntimeLogDetail';
import DiskUsage from './pages/DiskUsage';

// Mobile Pages
import MobileFTSOOverview from './pages/mobile/FTSOOverview';
import MobileFooter from './components/MobileFooter';
import MobileHeader from './components/MobileHeader';
import NodesMobile from './pages/mobile/NodesMobile';

// Contexts
import { StatusProvider } from './context/StatusContext';
import { SidebarProvider, useSidebar } from './context/SidebarContext';
import { AuthProvider } from './context/AuthContext';

import './index.css';
import './styles/mobile/index.css';
import './styles/loading.css';
import './styles/login.css';
import { useEffect, useMemo } from 'react';
import { prefetchData } from './hooks/prefetch';
import { getApiBase } from './utils/apiBase';

// Frontend feature toggle from build-time env
const RUNTIME_LOGS_ENABLED = (() => {
  try {
    const raw = (process.env.REACT_APP_ENABLE_RUNTIME_LOGS || '').trim().toLowerCase();
    return raw === '1' || raw === 'true' || raw === 'yes' || raw === 'on';
  } catch {
    return false;
  }
})();

const Layout = React.memo(({ children, isMobile, isLoading }) => {
  const { isOpen } = useSidebar();
  const location = useLocation();
  const isAuthPage = location.pathname === '/login' || location.pathname === '/change-password';

  if (isAuthPage) {
    return <div className="app">{children}</div>;
  }

  return (
    <div className="app">
      {!isMobile && (
        <>
          <Sidebar />
          <div className={`content-wrapper ${isOpen ? 'sidebar-expanded' : ''}`}>
            <Header isMobile={isMobile} />
            <div className="main-content">
              {isLoading ? (
                <div className="loading-content">
                  <LoadingAnimation />
                </div>
              ) : (
                children
              )}
            </div>
            <Footer />
          </div>
        </>
      )}
      {isMobile && (
        <div className="mobile-wrapper">
          <MobileHeader />
          {children}
          <MobileFooter />
        </div>
      )}
    </div>
  );
});

const AppRoutes = React.memo(({ isMobile }) => {
  const routes = [
    {
      path: "/ftso-submissions",
      Desktop: FTSOSubmissions,
      Mobile: MobileFTSOOverview
    },
    {
      path: "/disk-usage",
      Desktop: DiskUsage,
      Mobile: DiskUsage
    },
    {
      path: "/ftso-performance",
      Desktop: FTSOPerformance,
      Mobile: () => <Navigate to="/ftso-submissions" replace />
    },
    {
      path: "/fast-updates",
      Desktop: FastUpdates,
      Mobile: FastUpdates
    },
    {
      path: "/fdc-submissions",
      Desktop: FDCSubmissions,
      Mobile: FDCSubmissions
    },
    {
      path: "/ftso-summary",
      Desktop: FTSOSummary,
      Mobile: FTSOSummary
    },
    {
      path: "/nodes",
      Desktop: Nodes,
      Mobile: NodesMobile
    },
    {
      path: "/indexers",
      Desktop: Indexers,
      Mobile: Indexers
    },
    {
      path: "/wallets",
      Desktop: Wallets,
      Mobile: Wallets
    },
    {
      path: "/validators",
      Desktop: Validators,
      Mobile: Validators
    },
  ];

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/change-password" element={
        <ProtectedRoute>
          <ChangePassword />
        </ProtectedRoute>
      } />
      <Route path="/" element={<Navigate to="/ftso-submissions" replace />} />
      {RUNTIME_LOGS_ENABLED && (
        <Route 
          path="/runtime-logs/:instanceId/:container" 
          element={
            <ProtectedRoute>
              <RuntimeLogDetail />
            </ProtectedRoute>
          } 
        />
      )}
      {RUNTIME_LOGS_ENABLED && (
        <Route 
          path="/runtime-logs" 
          element={
            <ProtectedRoute>
              {isMobile ? <RuntimeLogs isMobile={isMobile} /> : <RuntimeLogs isMobile={isMobile} />}
            </ProtectedRoute>
          }
        />
      )}
      {routes.map(({ path, Desktop, Mobile }) => (
        <Route 
          key={path}
          path={path}
          element={
            <ProtectedRoute>
              {isMobile ? <Mobile isMobile={isMobile} /> : <Desktop isMobile={isMobile} />}
            </ProtectedRoute>
          }
        />
      ))}
    </Routes>
  );
});

function App() {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const API = getApiBase();

  // Prefetch heavy endpoints so first render is instant when navigating
  useEffect(() => {
    const prefetchHeavy = async () => {
      // FTSO submissions
      prefetchData('/submission_summary.json');
      prefetchData('/ftso_performance_dashboard.json');
      // FDC
      prefetchData('/fdc_submission_summary.json');
    };
    prefetchHeavy();
  }, [API]);

  return (
    <AuthProvider>
      <StatusProvider>
        <SidebarProvider>
          <Router>
            <Layout isMobile={isMobile}>
              <InitialStatusCheck />
              <StatusMonitor />
              <AppRoutes isMobile={isMobile} />
            </Layout>
          </Router>
        </SidebarProvider>
      </StatusProvider>
    </AuthProvider>
  );
}

export default App;